import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { LayoutGrid, Briefcase, Repeat, GitBranch, Sparkles, Loader2, Users, Euro, FileText, Save, Percent, Wrench, ShoppingCart, TrendingUp, HandCoins, CalendarDays, MapPin, PenLine } from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import api, { apiError, API } from "@/lib/api";

const STATUS_LABEL = { available: "À assigner", claimed: "Acceptée", in_progress: "En cours", completed: "Terminée", cancelled: "Annulée" };
const STATUS_CLS = { available: "bg-slate-100 text-slate-600", claimed: "bg-sky-100 text-sky-700", in_progress: "bg-amber-100 text-amber-700", completed: "bg-emerald-100 text-emerald-700" };

const eur = (v) => `${Number(v || 0).toLocaleString("fr-FR", { maximumFractionDigits: 2 })}€`;

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const qc = useQueryClient();
  const { data: stats } = useQuery({ queryKey: ["admin-overview"], queryFn: async () => (await api.get("/admin/overview")).data });
  const { data: missions = [] } = useQuery({ queryKey: ["admin-missions"], queryFn: async () => (await api.get("/admin/missions")).data });
  const { data: subs = [] } = useQuery({ queryKey: ["admin-subs"], queryFn: async () => (await api.get("/admin/subscriptions")).data });
  const { data: leads = [] } = useQuery({ queryKey: ["admin-leads"], queryFn: async () => (await api.get("/admin/leads")).data });
  const { data: technicians = [] } = useQuery({ queryKey: ["admin-technicians"], queryFn: async () => (await api.get("/admin/technicians")).data });
  const { data: poseurs = [] } = useQuery({ queryKey: ["admin-poseurs"], queryFn: async () => (await api.get("/admin/poseurs")).data });
  const { data: leadSales = [] } = useQuery({ queryKey: ["admin-lead-sales"], queryFn: async () => (await api.get("/admin/lead-sales")).data });
  const { data: planning = [] } = useQuery({ queryKey: ["admin-planning"], queryFn: async () => (await api.get("/admin/planning")).data });
  const { data: analytics } = useQuery({ queryKey: ["admin-analytics"], queryFn: async () => (await api.get("/admin/analytics")).data });

  const nav = [
    { key: "overview", label: "Tableau", icon: LayoutGrid, onClick: () => setTab("overview") },
    { key: "finances", label: "Finances", icon: TrendingUp, onClick: () => setTab("finances") },
    { key: "planning", label: "Planning", icon: CalendarDays, onClick: () => setTab("planning") },
    { key: "missions", label: "Missions", icon: Briefcase, onClick: () => setTab("missions") },
    { key: "techs", label: "Techniciens", icon: Wrench, onClick: () => setTab("techs") },
    { key: "leads", label: "Triage leads", icon: GitBranch, onClick: () => setTab("leads") },
    { key: "leadsales", label: "Ventes de leads", icon: ShoppingCart, onClick: () => setTab("leadsales") },
    { key: "subs", label: "Contrats", icon: Repeat, onClick: () => setTab("subs") },
    { key: "seo", label: "SEO/Blog", icon: Sparkles, onClick: () => setTab("seo") },
    { key: "legal", label: "Pages légales", icon: FileText, onClick: () => setTab("legal") },
  ];

  return (
    <DashboardShell title="Back-office Admin" nav={nav} active={tab} accent="#263871">
      {tab === "overview" && <Overview stats={stats} analytics={analytics} />}
      {tab === "finances" && <Finances stats={stats} qc={qc} />}
      {tab === "planning" && <Planning missions={planning} />}
      {tab === "missions" && <MissionsTable missions={missions} technicians={technicians} qc={qc} />}
      {tab === "techs" && <Technicians technicians={technicians} />}
      {tab === "leads" && <LeadsTriage leads={leads} poseurs={poseurs} qc={qc} />}
      {tab === "leadsales" && <LeadSales sales={leadSales} />}
      {tab === "subs" && <SubsTable subs={subs} />}
      {tab === "seo" && <Seo qc={qc} />}
      {tab === "legal" && <LegalEditor />}
    </DashboardShell>
  );
}

function BonIntervention({ mission }) {
  const photos = (arr) => (arr || []).map((p) => `${API}/files/${p}`);
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button data-testid={`bon-${mission.id}`} size="sm" variant="outline" className="h-8 rounded-full"><FileText className="mr-1 h-3.5 w-3.5" />Bon d'intervention</Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>Bon d'intervention</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-2 rounded-xl bg-slate-50 p-3">
            <span className="text-slate-500">Client</span><span className="font-medium">{mission.client_name}</span>
            <span className="text-slate-500">Prestation</span><span className="font-medium">{mission.nb_clim} clim · {mission.service_type}</span>
            <span className="text-slate-500">Adresse</span><span className="font-medium">{mission.address}</span>
            <span className="text-slate-500">Date</span><span className="font-medium">{mission.scheduled_date} {mission.scheduled_time || ""}</span>
            <span className="text-slate-500">Technicien</span><span className="font-medium">{mission.technician_name || "—"}</span>
            <span className="text-slate-500">Montant</span><span className="font-medium">{eur(mission.price_total)}</span>
          </div>
          <div>
            <p className="mb-2 font-semibold text-slate-700">Photos AVANT</p>
            <div className="flex flex-wrap gap-2">
              {photos(mission.photos_before).length ? photos(mission.photos_before).map((u, i) => <img key={i} src={u} alt="avant" className="h-20 w-20 rounded-lg border object-cover" />) : <span className="text-slate-400">Aucune</span>}
            </div>
          </div>
          <div>
            <p className="mb-2 font-semibold text-slate-700">Photos APRÈS</p>
            <div className="flex flex-wrap gap-2">
              {photos(mission.photos_after).length ? photos(mission.photos_after).map((u, i) => <img key={i} src={u} alt="après" className="h-20 w-20 rounded-lg border object-cover" />) : <span className="text-slate-400">Aucune</span>}
            </div>
          </div>
          <div>
            <p className="mb-2 font-semibold text-slate-700">Signature client</p>
            {mission.signature ? (
              <div className="rounded-xl border border-slate-200 p-2">
                <img src={mission.signature} alt="signature" className="mx-auto h-28 object-contain" />
                <p className="mt-1 text-center text-xs text-slate-400">Signé par {mission.signer_name || mission.client_name}{mission.signed_at ? ` le ${new Date(mission.signed_at).toLocaleDateString("fr-FR")}` : ""}</p>
              </div>
            ) : <span className="text-slate-400">Pas encore signé</span>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function UpsellButton({ clientId }) {
  const [sent, setSent] = useState(false);
  const send = async () => {
    try { await api.post(`/admin/upsell/${clientId}`); toast.success("Offre d'abonnement envoyée au client"); setSent(true); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Button data-testid={`upsell-${clientId}`} size="sm" variant="outline" disabled={sent} onClick={send} className="h-8 rounded-full border-amber-300 text-amber-600 hover:bg-amber-50">
      <Repeat className="mr-1 h-3.5 w-3.5" />{sent ? "Envoyé" : "Proposer abo"}
    </Button>
  );
}

function Planning({ missions }) {
  const byDate = {};
  missions.forEach((m) => { (byDate[m.scheduled_date || "Sans date"] ||= []).push(m); });
  const dates = Object.keys(byDate).sort();
  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-600">Planning de toutes les interventions : technicien, client, prestation, et bon d'intervention signé avec photos avant/après.</p>
      {dates.length === 0 && <Empty text="Aucune intervention planifiée." />}
      {dates.map((d) => (
        <div key={d}>
          <h3 className="mb-3 flex items-center gap-2 text-sm font-bold text-[#263871]"><CalendarDays className="h-4 w-4 text-[#1D9BF0]" />{d === "Sans date" ? d : new Date(d).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</h3>
          <div className="space-y-2">
            {byDate[d].map((m) => (
              <div key={m.id} data-testid={`planning-${m.id}`} className="flex flex-wrap items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4">
                <div className="w-14 shrink-0 text-center">
                  <p className="text-lg font-bold text-[#263871]">{m.scheduled_time || "—"}</p>
                  <p className="text-[10px] text-slate-400">{m.duration_minutes ? `${m.duration_minutes}min` : ""}</p>
                </div>
                <div className="min-w-[180px] flex-1">
                  <p className="font-semibold text-[#263871]">{m.client_name} · {m.nb_clim} clim · {m.service_type}</p>
                  <p className="flex items-center gap-1 text-sm text-slate-500"><MapPin className="h-3.5 w-3.5" />{m.address}</p>
                </div>
                <div className="flex items-center gap-1.5 text-sm text-slate-600"><Wrench className="h-4 w-4 text-emerald-500" />{m.technician_name || <span className="text-amber-600">Non assigné</span>}</div>
                <Badge className={STATUS_CLS[m.status] || "bg-slate-100"}>{STATUS_LABEL[m.status] || m.status}</Badge>
                {m.signature && <span className="flex items-center gap-1 text-xs font-semibold text-emerald-600"><PenLine className="h-3.5 w-3.5" />Signé</span>}
                <BonIntervention mission={m} />
                {m.client_id && <UpsellButton clientId={m.client_id} />}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Stat({ label, value, icon: Icon, accent, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{label}</span>
        <Icon className="h-5 w-5" style={{ color: accent }} />
      </div>
      <p className="mt-2 text-2xl font-extrabold text-[#263871] sm:text-3xl">{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );
}

function ChangeBadge({ value }) {
  const up = value >= 0;
  return <span className={`text-xs font-bold ${up ? "text-emerald-600" : "text-red-500"}`}>{up ? "▲" : "▼"} {Math.abs(value)}%</span>;
}

function Overview({ stats, analytics }) {
  if (!stats) return <p className="text-slate-500">Chargement…</p>;
  const months = analytics?.months || [];
  const cmp = analytics?.comparison;
  const postal = analytics?.postal || [];
  const mix = analytics?.mix;
  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-400">Finances</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="CA clients en ligne" value={eur(stats.total_client_revenue)} icon={Euro} accent="#1D9BF0" hint="Missions payées + abonnements" />
          <Stat label="Commission plateforme" value={eur(stats.total_commission)} icon={HandCoins} accent="#10B981" hint={`Taux ${Math.round(stats.commission_rate * 100)}% + ventes leads`} />
          <Stat label="Ventes de leads" value={eur(stats.lead_revenue)} icon={ShoppingCart} accent="#8B5CF6" hint={`${stats.lead_sales_count} lead(s) vendu(s)`} />
          <Stat label="Reversé techniciens" value={eur(stats.technician_payouts)} icon={Wrench} accent="#F59E0B" />
        </div>
      </div>

      {months.length > 0 && (
        <Card title="Évolution du chiffre d'affaires (12 mois)">
          {cmp && (
            <div className="mb-4 flex flex-wrap gap-6">
              <div><p className="text-xs text-slate-500">CA ce mois</p><p className="text-2xl font-extrabold text-[#263871]">{eur(cmp.current.ca_client)} <ChangeBadge value={cmp.ca_change} /></p><p className="text-xs text-slate-400">vs {eur(cmp.previous.ca_client)} le mois dernier</p></div>
              <div><p className="text-xs text-slate-500">Commission ce mois</p><p className="text-2xl font-extrabold text-emerald-600">{eur(cmp.current.commission)} <ChangeBadge value={cmp.commission_change} /></p><p className="text-xs text-slate-400">vs {eur(cmp.previous.commission)} le mois dernier</p></div>
            </div>
          )}
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={months} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
              <defs>
                <linearGradient id="gCa" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#1D9BF0" stopOpacity={0.4} /><stop offset="100%" stopColor="#1D9BF0" stopOpacity={0} /></linearGradient>
                <linearGradient id="gCom" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10B981" stopOpacity={0.4} /><stop offset="100%" stopColor="#10B981" stopOpacity={0} /></linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip formatter={(v) => eur(v)} />
              <Area type="monotone" dataKey="ca_client" name="CA clients" stroke="#1D9BF0" fill="url(#gCa)" strokeWidth={2} />
              <Area type="monotone" dataKey="commission" name="Commission" stroke="#10B981" fill="url(#gCom)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        {postal.length > 0 && (
          <Card title="CA & passages par code postal">
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={postal} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                <XAxis dataKey="postal" tick={{ fontSize: 10 }} interval={0} angle={-30} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="interventions" name="Passages" fill="#1D9BF0" radius={[4, 4, 0, 0]} />
                <Bar dataKey="ca" name="CA (€)" fill="#10B981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
        {mix && (
          <Card title="À la demande vs Abonnement">
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-2xl border-2 border-[#1D9BF0]/30 bg-[#E0F2FE]/40 p-5">
                <p className="text-sm font-semibold text-[#1D9BF0]">À la demande</p>
                <p className="mt-2 text-3xl font-extrabold text-[#263871]">{mix.oneshot_count}</p>
                <p className="text-xs text-slate-500">interventions · {eur(mix.oneshot_revenue)} encaissés</p>
              </div>
              <div className="rounded-2xl border-2 border-amber-300 bg-amber-50 p-5">
                <p className="text-sm font-semibold text-amber-600">Abonnement</p>
                <p className="mt-2 text-3xl font-extrabold text-[#263871]">{mix.abo_count}</p>
                <p className="text-xs text-slate-500">contrats actifs · {eur(mix.abo_mrr)}/mois</p>
              </div>
            </div>
            <p className="mt-4 text-xs text-slate-400">La fréquence de passage par zone est visible dans le graphique « par code postal » (nombre de passages).</p>
          </Card>
        )}
      </div>

      <div>
        <h3 className="mb-3 text-sm font-bold uppercase tracking-wider text-slate-400">Activité</h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Stat label="Missions totales" value={stats.missions_total} icon={Briefcase} accent="#1D9BF0" hint={`${stats.missions_available} dispo · ${stats.missions_completed} faites`} />
          <Stat label="Contrats actifs" value={stats.subscriptions} icon={Repeat} accent="#F59E0B" hint={`MRR ${eur(stats.sub_mrr)}`} />
          <Stat label="Nouveaux leads" value={stats.leads_new} icon={GitBranch} accent="#8B5CF6" />
          <Stat label="Réseau" value={`${stats.intervenants}/${stats.poseurs}`} icon={Users} accent="#263871" hint="Techniciens / Installateurs" />
        </div>
      </div>
    </div>
  );
}

function Finances({ stats, qc }) {
  const [rate, setRate] = useState("");
  const [shared, setShared] = useState("");
  const [excl, setExcl] = useState("");
  const [busy, setBusy] = useState(false);
  const { data: settings } = useQuery({ queryKey: ["admin-settings"], queryFn: async () => (await api.get("/admin/settings")).data });
  useEffect(() => {
    if (settings) { setRate(Math.round(settings.commission_rate * 100)); setShared(settings.lead_price_shared); setExcl(settings.lead_price_exclusive); }
  }, [settings]);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", { commission_rate: Number(rate) / 100, lead_price_shared: Number(shared), lead_price_exclusive: Number(excl) });
      toast.success("Paramètres enregistrés");
      qc.invalidateQueries({ queryKey: ["admin-settings"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <Card title="Modèle économique">
        <p className="mb-4 text-sm text-slate-600">La plateforme prélève une <strong>commission en % sur le paiement en ligne du client</strong> (intervention ponctuelle ou abonnement). Le reste est reversé au technicien. Les leads d'installation sont vendus aux installateurs à un prix fixe.</p>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <Label className="flex items-center gap-1"><Percent className="h-4 w-4" />Commission (%)</Label>
            <Input data-testid="commission-rate-input" type="number" min="0" max="90" value={rate} onChange={(e) => setRate(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>Prix lead partagé (€)</Label>
            <Input data-testid="lead-shared-input" type="number" min="0" value={shared} onChange={(e) => setShared(e.target.value)} className="mt-1" />
          </div>
          <div>
            <Label>Prix lead exclusif (€)</Label>
            <Input data-testid="lead-exclusive-input" type="number" min="0" value={excl} onChange={(e) => setExcl(e.target.value)} className="mt-1" />
          </div>
        </div>
        <Button data-testid="settings-save-btn" onClick={save} disabled={busy} className="mt-5 rounded-full bg-[#263871] hover:bg-[#35508F]">
          {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enregistrement…</> : <><Save className="mr-2 h-4 w-4" />Enregistrer</>}
        </Button>
      </Card>

      {stats && (
        <Card title="Détail des revenus">
          <Table>
            <TableHeader><TableRow><TableHead>Source</TableHead><TableHead>CA encaissé client</TableHead><TableHead>Commission plateforme</TableHead></TableRow></TableHeader>
            <TableBody>
              <TableRow><TableCell>Interventions ponctuelles</TableCell><TableCell>{eur(stats.mission_gmv)}</TableCell><TableCell className="font-semibold text-emerald-600">{eur(stats.mission_commission)}</TableCell></TableRow>
              <TableRow><TableCell>Abonnements (mensuel)</TableCell><TableCell>{eur(stats.sub_mrr)}</TableCell><TableCell className="font-semibold text-emerald-600">{eur(stats.sub_commission)}</TableCell></TableRow>
              <TableRow><TableCell>Ventes de leads installation</TableCell><TableCell>—</TableCell><TableCell className="font-semibold text-emerald-600">{eur(stats.lead_cash)}</TableCell></TableRow>
              <TableRow className="border-t-2"><TableCell className="font-bold">Total</TableCell><TableCell className="font-bold">{eur(stats.total_client_revenue)}</TableCell><TableCell className="font-extrabold text-emerald-700">{eur(stats.total_commission)}</TableCell></TableRow>
            </TableBody>
          </Table>
          <p className="mt-3 text-xs text-slate-400">Calculé sur les missions payées, abonnements actifs et leads réellement vendus.</p>
        </Card>
      )}
    </div>
  );
}

function AssignTech({ mission, technicians, qc }) {
  const assign = async (technician_id) => {
    try {
      await api.post(`/admin/missions/${mission.id}/assign`, { technician_id });
      toast.success("Mission attribuée");
      qc.invalidateQueries({ queryKey: ["admin-missions"] });
      qc.invalidateQueries({ queryKey: ["admin-technicians"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <Select onValueChange={assign}>
      <SelectTrigger data-testid={`assign-mission-${mission.id}`} className="h-8 w-36"><SelectValue placeholder="Assigner…" /></SelectTrigger>
      <SelectContent>
        {technicians.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}{t.base_city ? ` · ${t.base_city}` : ""}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}

function MissionsTable({ missions, technicians, qc }) {
  return (
    <Card title="Toutes les missions">
      <Table>
        <TableHeader><TableRow><TableHead>Client</TableHead><TableHead>Clims</TableHead><TableHead>Adresse</TableHead><TableHead>Statut</TableHead><TableHead>Technicien</TableHead><TableHead>Total</TableHead><TableHead>Commission</TableHead><TableHead>Reversé</TableHead><TableHead>Paiement</TableHead></TableRow></TableHeader>
        <TableBody>
          {missions.map((m) => (
            <TableRow key={m.id} data-testid={`admin-mission-${m.id}`}>
              <TableCell>{m.client_name}</TableCell><TableCell>{m.nb_clim}</TableCell>
              <TableCell className="max-w-[150px] truncate">{m.address}</TableCell>
              <TableCell><Badge variant="outline">{m.status}</Badge></TableCell>
              <TableCell>{m.technician_name || (m.status === "available" ? <AssignTech mission={m} technicians={technicians} qc={qc} /> : "—")}</TableCell>
              <TableCell>{eur(m.price_total)}</TableCell>
              <TableCell className="font-semibold text-emerald-600">{eur(m.platform_margin)}</TableCell>
              <TableCell>{eur(m.technician_payout)}</TableCell>
              <TableCell><Badge className={m.payment_status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>{m.payment_status}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {missions.length === 0 && <Empty text="Aucune mission." />}
    </Card>
  );
}

function Technicians({ technicians }) {
  return (
    <Card title="Suivi des techniciens">
      <Table>
        <TableHeader><TableRow><TableHead>Technicien</TableHead><TableHead>Zone</TableHead><TableHead>Missions</TableHead><TableHead>Terminées</TableHead><TableHead>CA généré</TableHead><TableHead>Reversé</TableHead></TableRow></TableHeader>
        <TableBody>
          {technicians.map((t) => (
            <TableRow key={t.id} data-testid={`admin-tech-${t.id}`}>
              <TableCell className="font-medium">{t.name}</TableCell>
              <TableCell className="text-sm text-slate-500">{t.base_city || "—"}</TableCell>
              <TableCell>{t.missions}</TableCell>
              <TableCell><Badge className="bg-emerald-100 text-emerald-700">{t.completed}</Badge></TableCell>
              <TableCell>{eur(t.revenue)}</TableCell>
              <TableCell className="font-semibold">{eur(t.payout)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {technicians.length === 0 && <Empty text="Aucun technicien." />}
    </Card>
  );
}

function SellLead({ lead, poseurs, qc }) {
  const [poseurId, setPoseurId] = useState("");
  const [mode, setMode] = useState("shared");
  const sell = async () => {
    if (!poseurId) return toast.error("Choisissez un installateur");
    try {
      await api.post(`/admin/leads/${lead.id}/assign`, { poseur_id: poseurId, mode });
      toast.success("Lead vendu à l'installateur");
      qc.invalidateQueries({ queryKey: ["admin-leads"] });
      qc.invalidateQueries({ queryKey: ["admin-lead-sales"] });
      qc.invalidateQueries({ queryKey: ["admin-overview"] });
    } catch (e) { toast.error(apiError(e)); }
  };
  const sold = lead.exclusive_owner || (lead.buyers || []).length >= 3;
  if (sold) return <span className="text-xs text-slate-400">Vendu</span>;
  return (
    <div className="flex items-center gap-1.5">
      <Select value={poseurId} onValueChange={setPoseurId}>
        <SelectTrigger data-testid={`sell-lead-${lead.id}`} className="h-8 w-32"><SelectValue placeholder="Installateur" /></SelectTrigger>
        <SelectContent>{poseurs.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={mode} onValueChange={setMode}>
        <SelectTrigger className="h-8 w-24"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="shared">Partagé</SelectItem><SelectItem value="exclusive">Exclusif</SelectItem></SelectContent>
      </Select>
      <Button data-testid={`sell-lead-btn-${lead.id}`} size="sm" onClick={sell} className="h-8 rounded-full bg-emerald-600 px-3 hover:bg-emerald-700">Vendre</Button>
    </div>
  );
}

function LeadsTriage({ leads, poseurs, qc }) {
  const setStatus = async (id, status) => {
    try { await api.post(`/admin/leads/${id}/triage`, { status }); qc.invalidateQueries({ queryKey: ["admin-leads"] }); toast.success("Lead mis à jour"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const installs = leads.filter((l) => l.type === "installation");
  return (
    <Card title="Gare de triage & vente des leads">
      <Table>
        <TableHeader><TableRow><TableHead>Client</TableHead><TableHead>Clims</TableHead><TableHead>Adresse</TableHead><TableHead>Statut</TableHead><TableHead>Acheteurs</TableHead><TableHead>Vendre à un installateur</TableHead></TableRow></TableHeader>
        <TableBody>
          {installs.map((l) => (
            <TableRow key={l.id} data-testid={`admin-lead-${l.id}`}>
              <TableCell>{l.client_name}</TableCell>
              <TableCell>{l.nb_clim}</TableCell>
              <TableCell className="max-w-[130px] truncate">{l.address}</TableCell>
              <TableCell>
                <Select value={l.status} onValueChange={(v) => setStatus(l.id, v)}>
                  <SelectTrigger data-testid={`triage-${l.id}`} className="h-8 w-28"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">Nouveau</SelectItem>
                    <SelectItem value="triaged">Trié</SelectItem>
                    <SelectItem value="shared_open">Partagé</SelectItem>
                    <SelectItem value="closed">Fermé</SelectItem>
                  </SelectContent>
                </Select>
              </TableCell>
              <TableCell><Badge variant="outline">{(l.buyers || []).length}/3</Badge></TableCell>
              <TableCell><SellLead lead={l} poseurs={poseurs} qc={qc} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {installs.length === 0 && <Empty text="Aucun lead d'installation." />}
    </Card>
  );
}

function LeadSales({ sales }) {
  const total = sales.reduce((s, x) => s + Number(x.amount || 0), 0);
  return (
    <Card title={`Ventes de leads — ${eur(total)}`}>
      <Table>
        <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Installateur</TableHead><TableHead>Adresse</TableHead><TableHead>Clims</TableHead><TableHead>Mode</TableHead><TableHead>Montant</TableHead><TableHead>Paiement</TableHead></TableRow></TableHeader>
        <TableBody>
          {sales.map((s, i) => (
            <TableRow key={i} data-testid={`lead-sale-${i}`}>
              <TableCell className="text-sm">{s.at ? new Date(s.at).toLocaleDateString("fr-FR") : "—"}</TableCell>
              <TableCell className="font-medium">{s.poseur_name}</TableCell>
              <TableCell className="max-w-[150px] truncate">{s.address}</TableCell>
              <TableCell>{s.nb_clim}</TableCell>
              <TableCell><Badge variant="outline">{s.mode === "exclusive" ? "Exclusif" : "Partagé"}</Badge></TableCell>
              <TableCell className="font-semibold text-emerald-600">{eur(s.amount)}</TableCell>
              <TableCell><Badge className="bg-slate-100 text-slate-600">{s.paid_with === "admin_sale" ? "Vente admin" : s.paid_with === "stripe" ? "Stripe" : "Avoir"}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {sales.length === 0 && <Empty text="Aucune vente de lead pour l'instant." />}
    </Card>
  );
}

function SubsTable({ subs }) {
  return (
    <Card title="Contrats d'entretien">
      <Table>
        <TableHeader><TableRow><TableHead>Client</TableHead><TableHead>Fréquence</TableHead><TableHead>Clims</TableHead><TableHead>Prochaine visite</TableHead><TableHead>Mensualité</TableHead><TableHead>Commission</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
        <TableBody>
          {subs.map((s) => (
            <TableRow key={s.id} data-testid={`admin-sub-${s.id}`}>
              <TableCell>{s.client_name}</TableCell><TableCell>{s.frequency}</TableCell><TableCell>{s.nb_clim}</TableCell>
              <TableCell>{s.next_visit}</TableCell><TableCell>{eur(s.price)}</TableCell>
              <TableCell className="font-semibold text-emerald-600">{s.commission != null ? eur(s.commission) : "—"}</TableCell>
              <TableCell><Badge className="bg-emerald-100 text-emerald-700">{s.status}</Badge></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {subs.length === 0 && <Empty text="Aucun contrat." />}
    </Card>
  );
}

function Seo({ qc }) {
  const [busy, setBusy] = useState(false);
  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/blog/generate", { limit: 6 });
      toast.success(`${data.count} article(s) généré(s) !`);
      qc.invalidateQueries({ queryKey: ["blog"] });
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Card title="Machine à contenu SEO">
      <p className="text-sm text-slate-600">Génère automatiquement des articles de blog optimisés en croisant <strong>villes × prestations</strong> (ex: « Entretien de climatisation à Lyon »). Idéal pour capter du trafic local et générer des demandes de RDV.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        {["Paris", "Lyon", "Marseille", "Toulouse", "Bordeaux", "Nice"].map((c) => <Badge key={c} variant="outline">{c}</Badge>)}
        <span className="text-slate-300">×</span>
        {["Entretien", "Nettoyage", "Installation", "Dépannage"].map((s) => <Badge key={s} className="bg-[#E0F2FE] text-[#263871]">{s}</Badge>)}
      </div>
      <Button data-testid="generate-blog-btn" onClick={generate} disabled={busy} className="mt-6 rounded-full bg-[#263871] hover:bg-[#35508F]">
        {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Génération en cours…</> : <><Sparkles className="mr-2 h-4 w-4" />Générer 6 articles</>}
      </Button>
      <p className="mt-4 text-xs text-slate-400">Un <code>sitemap.xml</code> est exposé automatiquement à la racine pour l'indexation Google.</p>
    </Card>
  );
}

function LegalEditor() {
  const PAGES = [
    { slug: "mentions-legales", label: "Mentions légales" },
    { slug: "cgv", label: "CGV" },
    { slug: "confidentialite", label: "Politique de confidentialité" },
  ];
  const [slug, setSlug] = useState("mentions-legales");
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const { data } = useQuery({ queryKey: ["content-admin", slug], queryFn: async () => (await api.get(`/content/${slug}`)).data });
  useEffect(() => { if (data) { setTitle(data.title || ""); setBody(data.body || ""); } }, [data]);

  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/content/${slug}`, { title, body }); toast.success("Page enregistrée"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Card title="Édition des pages légales">
      <p className="mb-4 text-sm text-slate-600">Modifiez le contenu des pages légales. Le formatage Markdown est supporté : <code>## Titre</code> pour les sections, <code>**gras**</code>, et <code>- </code> pour les listes.</p>
      <div className="space-y-4">
        <div className="sm:w-72">
          <Label>Page à modifier</Label>
          <Select value={slug} onValueChange={setSlug}>
            <SelectTrigger data-testid="legal-page-select" className="mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {PAGES.map((p) => <SelectItem key={p.slug} value={p.slug}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Titre</Label>
          <Input data-testid="legal-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1" />
        </div>
        <div>
          <Label>Contenu (Markdown)</Label>
          <Textarea data-testid="legal-body-input" value={body} onChange={(e) => setBody(e.target.value)} rows={18} className="mt-1 font-mono text-sm" />
        </div>
        <Button data-testid="legal-save-btn" onClick={save} disabled={busy} className="rounded-full bg-[#263871] hover:bg-[#35508F]">
          {busy ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Enregistrement…</> : <><Save className="mr-2 h-4 w-4" />Enregistrer</>}
        </Button>
      </div>
    </Card>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6">
      <h2 className="mb-4 text-lg font-bold text-[#263871]">{title}</h2>
      <div className="overflow-x-auto">{children}</div>
    </div>
  );
}
function Empty({ text }) {
  return <p className="py-8 text-center text-slate-400">{text}</p>;
}
